// FILE: UserManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from './config';
import MultiSelect from './MultiSelect';
import './UserManagement.css'; // Importa o CSS global para UserManagement.js

// Removido o prop 'onBack' conforme solicitado
function UserManagement({ token }) {
  const [users, setUsers] = useState([]);
  const [servers, setServers] = useState([]);
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'user',
    ramais_permitidos: '',
    nome_cliente: '',
    issabel_server_id: '',
    filas_permitidas: '' // Adicionado: Novo campo para filas permitidas
  });
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingServers, setLoadingServers] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    fetchUsers();
    fetchServers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: {
          Authorization: 'Bearer ' + token
        }
      });
      
      console.log("Usuários recebidos:", response.data);
      setUsers(response.data);
    } catch (err) {
      console.error("Erro ao buscar usuários:", err);
      setError('Erro ao buscar usuários');
    }
  };

  const fetchServers = async () => {
    setLoadingServers(true);
    try {
      // Buscar servidores ativos
      const response = await axios.get(`${API_URL}/users/servers/active`, {
        headers: {
          Authorization: 'Bearer ' + token
        }
      });
      
      console.log("Servidores ativos recebidos:", response.data);
      setServers(response.data);
    } catch (err) {
      console.error("Erro ao buscar servidores ativos:", err);
      // Não mostrar erro na tela para não confundir o usuário
    } finally {
      setLoadingServers(false);
    }
  };

  // Busca os ramais do servidor selecionado (para os checkboxes)
  const fetchRamais = useCallback(async (serverId) => {
    const resp = await axios.get(`${API_URL}/issabelServers/${serverId}/ramais`, {
      headers: { Authorization: 'Bearer ' + token }
    });
    return resp.data || [];
  }, [token]);

  // Busca as filas do servidor selecionado (para os checkboxes)
  const fetchFilas = useCallback(async (serverId) => {
    const resp = await axios.get(`${API_URL}/issabelServers/${serverId}/filas`, {
      headers: { Authorization: 'Bearer ' + token }
    });
    return resp.data || [];
  }, [token]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Verificar campos obrigatórios
      if (!newUser.username.trim() || !newUser.password || !newUser.nome_cliente.trim()) {
        setError('Nome de usuário, senha e nome do cliente são obrigatórios.');
        setLoading(false);
        return;
      }

      // Garantir que ramais_permitidos é uma string (mesmo se vazia)
      const ramaisFormatados = newUser.ramais_permitidos.trim() 
        ? newUser.ramais_permitidos
            .split(',')
            .map(ramal => ramal.trim())
            .filter(ramal => ramal !== '')
            .join(', ')
        : ''; // Enviar string vazia, não null

      // Adicionado: Formatação para filas_permitidas
      const filasFormatadas = newUser.filas_permitidas.trim()
        ? newUser.filas_permitidas
            .split(',')
            .map(fila => fila.trim())
            .filter(fila => fila !== '')
            .join(', ')
        : ''; // String vazia em vez de null

      // Dados no formato esperado pelo servidor - nunca enviar null
      const userData = {
        username: newUser.username.trim(),
        password: newUser.password,
        role: newUser.role,
        nome_cliente: newUser.nome_cliente.trim(), // Sempre string, nunca vazia
      };
      
      // Adicionar ramais, filas e servidor apenas se o usuário não for admin
      if (newUser.role !== 'admin') {
        userData.ramais_permitidos = ramaisFormatados;
        userData.filas_permitidas = filasFormatadas; // Adicionado: Incluir filas permitidas
        if (newUser.issabel_server_id) {
          userData.issabel_server_id = newUser.issabel_server_id;
        }
      }

      const response = await axios.post(`${API_URL}/users`, userData, {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      
      setNewUser({ 
        username: '', 
        password: '', 
        role: 'user', 
        ramais_permitidos: '',
        nome_cliente: '',
        issabel_server_id: '',
        filas_permitidas: '' // Adicionado: Limpar campo após sucesso
      });
      setSuccess('Usuário criado com sucesso!');
      await fetchUsers();
    } catch (err) {
      console.error("Erro ao criar usuário:", err);
      setError(`Erro ao criar usuário: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Verificar campos obrigatórios
      if (!editingUser.nome_cliente || editingUser.nome_cliente.trim() === '') {
        setError('Nome do cliente é obrigatório.');
        setLoading(false);
        return;
      }

      // Formatação correta dos ramais - sempre string, mesmo se vazia
      const ramaisFormatados = editingUser.ramais_permitidos && editingUser.ramais_permitidos.trim()
        ? editingUser.ramais_permitidos
            .split(',')
            .map(ramal => ramal.trim())
            .filter(ramal => ramal !== '')
            .join(', ')
        : ''; // String vazia em vez de null

      // Adicionado: Formatação para filas_permitidas
      const filasFormatadas = editingUser.filas_permitidas && editingUser.filas_permitidas.trim()
        ? editingUser.filas_permitidas
            .split(',')
            .map(fila => fila.trim())
            .filter(fila => fila !== '')
            .join(', ')
        : ''; // String vazia em vez de null

      const updateData = {
        role: editingUser.role,
        nome_cliente: editingUser.nome_cliente.trim(),
      };
      
      // Adicionar ramais, filas e servidor apenas se o usuário não for admin
      if (editingUser.role !== 'admin') {
        updateData.ramais_permitidos = ramaisFormatados;
        updateData.filas_permitidas = filasFormatadas; // Adicionado: Incluir filas permitidas
        if (editingUser.issabel_server_id) {
          updateData.issabel_server_id = editingUser.issabel_server_id;
        } else {
          // Para remover o servidor (campo vazio no formulário)
          updateData.issabel_server_id = null;
        }
      }

      // Apenas incluir senha se foi fornecida
      if (editingUser.password) {
        updateData.password = editingUser.password;
      }

      // Enviar a requisição PUT
      const response = await axios.put(`${API_URL}/users/${editingUser.username}`, updateData, {
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json'
        }
      });
      
      setSuccess('Usuário atualizado com sucesso!');
      setEditingUser(null);
      await fetchUsers();
    } catch (err) {
      console.error("Erro ao atualizar usuário:", err);
      setError(`Erro ao atualizar usuário: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (window.confirm('Tem certeza que deseja excluir este usuário?')) {
      try {
        await axios.delete(`${API_URL}/users/${username}`, {
          headers: {
            Authorization: 'Bearer ' + token
          }
        });
        setSuccess('Usuário excluído com sucesso!');
        await fetchUsers();
      } catch (err) {
        console.error("Erro ao excluir usuário:", err);
        setError(`Erro ao excluir usuário: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const startEditing = (user) => {
    console.log("Iniciando edição do usuário:", user);
    setEditingUser({
      ...user,
      password: '',
      // Garantir que não sejam null
      ramais_permitidos: user.ramais_permitidos || '',
      nome_cliente: user.nome_cliente || '',
      issabel_server_id: user.issabel_server_id || '',
      filas_permitidas: user.filas_permitidas || '' // Adicionado: Popular campo de filas
    });
  };

  const cancelEditing = () => {
    setEditingUser(null);
  };
  
  // Função para obter o nome do servidor pelo ID
  const getServerNameById = (serverId) => {
    if (!serverId) return '';
    const server = servers.find(s => s.id === parseInt(serverId));
    return server ? server.name : '';
  };
  
  // Função para alternar o tipo de usuário (admin/user)
  const handleRoleChange = (e, isNewUser = true) => {
    const newRole = e.target.value;
    
    if (isNewUser) {
      // Para novo usuário
      if (newRole === 'admin') {
        // Se for admin, limpar e desabilitar campos de ramais, filas e servidor
        setNewUser({
          ...newUser, 
          role: newRole,
          ramais_permitidos: '',
          issabel_server_id: '',
          filas_permitidas: '' // Adicionado: Limpar filas se for admin
        });
      } else {
        setNewUser({...newUser, role: newRole});
      }
    } else {
      // Para usuário existente sendo editado
      if (newRole === 'admin') {
        // Se for admin, limpar e desabilitar campos de ramais, filas e servidor
        setEditingUser({
          ...editingUser, 
          role: newRole,
          ramais_permitidos: '',
          issabel_server_id: '',
          filas_permitidas: '' // Adicionado: Limpar filas se for admin
        });
      } else {
        setEditingUser({...editingUser, role: newRole});
      }
    }
  };

  // Ordena alfabeticamente e separa administradores dos usuários comuns.
  const byName = (a, b) => (a.username || '').localeCompare(b.username || '', 'pt-BR', { sensitivity: 'base' });

  // Busca por username, cliente, ramais, filas ou servidor.
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const matchesSearch = (u) => {
    if (!normalizedSearch) return true;
    const haystack = [
      u.username,
      u.nome_cliente,
      u.ramais_permitidos,
      u.filas_permitidas,
      u.issabel_server_name,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(normalizedSearch);
  };

  const admins = users.filter((u) => u.role === 'admin' && matchesSearch(u)).sort(byName);
  const operadores = users.filter((u) => u.role === 'operador' && matchesSearch(u)).sort(byName);
  const commonUsers = users.filter((u) => u.role === 'user' && matchesSearch(u)).sort(byName);

  const renderUserItem = (user) => (
    <div
      key={user.username}
      className={`userListItem ${user.username === (editingUser?.username) ? 'userListItem--editing' : ''}`}
    >
      <div className="userListItemInfo">
        <strong>{user.username}</strong>
        <span className="userRole">
          ({user.role === 'admin' ? 'Administrador' : user.role === 'operador' ? 'Operador' : 'Usuário'})
        </span>
        {user.nome_cliente && (
          <span className="clientName">
            Cliente: {user.nome_cliente}
          </span>
        )}
        {/* Mostrar ramais, filas e servidor apenas para usuários não-admin */}
        {user.role !== 'admin' && (
          <>
            <div className="userDetails">
              Ramais: {user.ramais_permitidos ? user.ramais_permitidos : 'nenhum acesso'}
            </div>
            <div className="userDetails">
              Filas: {user.filas_permitidas ? user.filas_permitidas : 'nenhum acesso'}
            </div>
            {user.issabel_server_id && (
              <div className="userDetails">
                PBX IP: {user.issabel_server_name || getServerNameById(user.issabel_server_id) || `ID: ${user.issabel_server_id}`}
              </div>
            )}
          </>
        )}
      </div>
      <div>
        <button
          onClick={() => startEditing(user)}
          className="button buttonSecondary buttonEdit"
        >
          Editar
        </button>
        <button
          onClick={() => handleDeleteUser(user.username)}
          className="button buttonDanger"
        >
          Excluir
        </button>
      </div>
    </div>
  );

  return (
    <div className="userManagementContainer">
      <h1 className="pageTitle">Gerenciamento de Usuários</h1>

      {success && <div className="message messageSuccess">{success}</div>}
      {error && <div className="message messageError">{error}</div>}

      {/* Formulário de Novo Usuário - escondido quando está editando um usuário */}
      {!editingUser && (
        <div style={{ marginBottom: '30px' }}>
          <h2 className="sectionTitle">Novo Usuário</h2>
          <form onSubmit={handleAddUser} className="formContainer">
            <div className="formGrid">
              <div className="formGroup">
                <label htmlFor="new-username" className="formLabel">
                  Nome de usuário: <span className="requiredIndicator">*</span>
                </label>
                <input
                  id="new-username"
                  type="text"
                  placeholder="Nome do usuário"
                  value={newUser.username}
                  onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                  className="formInput"
                  required
                />
              </div>
              
              <div className="formGroup">
                <label htmlFor="new-password" className="formLabel">
                  Senha: <span className="requiredIndicator">*</span>
                </label>
                <input
                  id="new-password"
                  type="password"
                  placeholder="Senha"
                  value={newUser.password}
                  onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  className="formInput"
                  required
                />
              </div>
              
              <div className="formGroup">
                <label htmlFor="new-client-name" className="formLabel">
                  Nome do Cliente: <span className="requiredIndicator">*</span>
                </label>
                <input
                  id="new-client-name"
                  type="text"
                  placeholder="Nome do cliente"
                  value={newUser.nome_cliente}
                  onChange={(e) => setNewUser({...newUser, nome_cliente: e.target.value})}
                  className="formInput"
                  required
                />
                <small className="formHelperText">
                  Campo obrigatório - não pode ficar vazio
                </small>
              </div>
              
              <div className="formGroup">
                <label htmlFor="new-role" className="formLabel">Tipo de usuário:</label>
                <select
                  id="new-role"
                  value={newUser.role}
                  onChange={(e) => handleRoleChange(e, true)}
                  className="formSelect"
                >
                  <option value="user">Usuário</option>
                  <option value="operador">Operador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              
              {/* Campos exibidos apenas para usuários não-admin */}
              {newUser.role !== 'admin' && (
                <>
                  <div className="formGroup">
                    <label htmlFor="new-server" className="formLabel">
                      PBX IP:
                    </label>
                    <select
                      id="new-server"
                      value={newUser.issabel_server_id}
                      onChange={(e) => setNewUser({...newUser, issabel_server_id: e.target.value})}
                      className="formSelect"
                      disabled={loadingServers}
                    >
                      <option value="">Selecione um PBX IP</option>
                      {servers.map(server => (
                        <option key={server.id} value={server.id}>
                          {server.name}
                        </option>
                      ))}
                    </select>
                    <small className="formHelperText">
                      {loadingServers 
                        ? 'Carregando servidores...' 
                        : 'Opcional: Servidor PBX IP que o usuário terá acesso'}
                    </small>
                  </div>

                  <div className="formGroupFull">
                    <MultiSelect
                      title="Ramais permitidos"
                      serverId={newUser.issabel_server_id}
                      value={newUser.ramais_permitidos}
                      onChange={(csv) => setNewUser({...newUser, ramais_permitidos: csv})}
                      fetchItems={fetchRamais}
                      itemKey={(i) => i.extension}
                      itemLabel={(i) => i.extension}
                      emptyText="Selecione um servidor para carregar os ramais."
                    />
                  </div>

                  <div className="formGroupFull">
                    <MultiSelect
                      title="Filas permitidas"
                      serverId={newUser.issabel_server_id}
                      value={newUser.filas_permitidas}
                      onChange={(csv) => setNewUser({...newUser, filas_permitidas: csv})}
                      fetchItems={fetchFilas}
                      itemKey={(i) => i.fila}
                      itemLabel={(i) => i.nome ? `${i.fila} - ${i.nome}` : i.fila}
                      emptyText="Selecione um servidor para carregar as filas."
                    />
                  </div>
                </>
              )}
              
              <div className="formActions">
                <button
                  type="submit"
                  disabled={loading}
                  className="button buttonPrimary"
                >
                  {loading ? 'Criando...' : 'Criar Usuário'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Formulário de Edição */}
      {editingUser && (
        <div style={{ marginBottom: '30px' }}>
          <h2 className="sectionTitle">Editar Usuário: {editingUser.username}</h2>
          <form onSubmit={handleUpdateUser} className="formContainer">
            <div className="formGrid">
              <div className="formGroup">
                <label htmlFor="edit-password" className="formLabel">Nova senha (deixe vazio para manter):</label>
                <input
                  id="edit-password"
                  type="password"
                  placeholder="Nova senha (opcional)"
                  value={editingUser.password || ''}
                  onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                  className="formInput"
                />
              </div>
              
              <div className="formGroup">
                <label htmlFor="edit-role" className="formLabel">Tipo de usuário:</label>
                <select
                  id="edit-role"
                  value={editingUser.role || 'user'}
                  onChange={(e) => handleRoleChange(e, false)}
                  className="formSelect"
                >
                  <option value="user">Usuário</option>
                  <option value="operador">Operador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
              
              <div className="formGroupFull">
                <label htmlFor="edit-client-name" className="formLabel">
                  Nome do Cliente: <span className="requiredIndicator">*</span>
                </label>
                <input
                  id="edit-client-name"
                  type="text"
                  placeholder="Nome do cliente"
                  value={editingUser.nome_cliente || ''}
                  onChange={(e) => setEditingUser({ ...editingUser, nome_cliente: e.target.value })}
                  className="formInput"
                  required
                />
                <small className="formHelperText">
                  Campo obrigatório - não pode ficar vazio
                </small>
              </div>
              
              {/* Campos exibidos apenas para usuários não-admin */}
              {editingUser.role !== 'admin' && (
                <>
                  <div className="formGroup">
                    <label htmlFor="edit-server" className="formLabel">
                      PBX IP:
                    </label>
                    <select
                      id="edit-server"
                      value={editingUser.issabel_server_id || ''}
                      onChange={(e) => setEditingUser({...editingUser, issabel_server_id: e.target.value})}
                      className="formSelect"
                      disabled={loadingServers}
                    >
                      <option value="">Selecione um PBX IP</option>
                      {servers.map(server => (
                        <option key={server.id} value={server.id}>
                          {server.name}
                        </option>
                      ))}
                    </select>
                    <small className="formHelperText">
                      {loadingServers 
                        ? 'Carregando servidores...' 
                        : 'Opcional: Servidor PBX IP que o usuário terá acesso'}
                    </small>
                  </div>

                  <div className="formGroupFull">
                    <MultiSelect
                      title="Ramais permitidos"
                      serverId={editingUser.issabel_server_id || ''}
                      value={editingUser.ramais_permitidos || ''}
                      onChange={(csv) => setEditingUser({...editingUser, ramais_permitidos: csv})}
                      fetchItems={fetchRamais}
                      itemKey={(i) => i.extension}
                      itemLabel={(i) => i.extension}
                      emptyText="Selecione um servidor para carregar os ramais."
                    />
                  </div>

                  <div className="formGroupFull">
                    <MultiSelect
                      title="Filas permitidas"
                      serverId={editingUser.issabel_server_id || ''}
                      value={editingUser.filas_permitidas || ''}
                      onChange={(csv) => setEditingUser({...editingUser, filas_permitidas: csv})}
                      fetchItems={fetchFilas}
                      itemKey={(i) => i.fila}
                      itemLabel={(i) => i.nome ? `${i.fila} - ${i.nome}` : i.fila}
                      emptyText="Selecione um servidor para carregar as filas."
                    />
                  </div>
                </>
              )}
              
              <div className="formActions">
                <button type="button" onClick={cancelEditing} className="button buttonSecondary">
                  Cancelar
                </button>
                <button type="submit" disabled={loading} className="button buttonPrimary">
                  {loading ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Usuários */}
      <div>
        <h2 className="sectionTitle">Lista de Usuários</h2>

        {/* Campo de busca de usuários */}
        {users.length > 0 && (
          <div className="userSearchWrapper">
            <input
              type="text"
              className="formInput userSearchInput"
              placeholder="Buscar por nome, cliente, ramal ou fila..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="button buttonSecondary"
                onClick={() => setSearchTerm('')}
              >
                Limpar
              </button>
            )}
          </div>
        )}

        <div className="userListContainer">
          {users.length === 0 ? (
            <p>Nenhum usuário encontrado.</p>
          ) : admins.length === 0 && operadores.length === 0 && commonUsers.length === 0 ? (
            <p className="userSearchEmpty">
              Nenhum usuário corresponde à busca "{searchTerm}".
            </p>
          ) : (
            <>
              {admins.length > 0 && (
                <>
                  <h3 className="userGroupTitle">Administradores</h3>
                  {admins.map(renderUserItem)}
                </>
              )}
              {operadores.length > 0 && (
                <>
                  <h3 className="userGroupTitle">Operadores</h3>
                  {operadores.map(renderUserItem)}
                </>
              )}
              {commonUsers.length > 0 && (
                <>
                  <h3 className="userGroupTitle">Usuários</h3>
                  {commonUsers.map(renderUserItem)}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
